import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiCall } from '../../services/apiUtil';
import { generateDocumentHtml } from '../utils/documentHtml';

export default function TournamentDocumentPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const type = location.pathname.endsWith('/waiver') ? 'waiver' : 'terms';
  const { theme } = useTheme();
  const [srcDoc, setSrcDoc] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const title = type === 'terms' ? 'Terms & Conditions' : 'Liability Waiver';

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiCall(`/tournaments/${id}/documents/${type}`);
        if (res.format === 'pdf') {
          setPdfUrl(res.url);
        } else if (res.format === 'json') {
          setSrcDoc(generateDocumentHtml(res.data));
        } else {
          // Legacy HTML document
          setSrcDoc(res.html);
        }
      } catch (err) {
        setError(err?.message?.includes('404') || err?.statusCode === 404
          ? 'No document has been created for this tournament yet.'
          : 'Failed to load document. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, type]);

  return (
    <div className={`min-h-screen ${theme.background}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${theme.backgroundSecondary} border-b ${theme.border} px-4 py-3 flex items-center gap-x-3`}>
        <button
          onClick={() => navigate(-1)}
          className={`p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${theme.textMuted}`}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <FileText className="w-5 h-5 text-blue-500" />
        <h1 className={`font-semibold text-base ${theme.text}`}>{title}</h1>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-3">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className={`text-sm ${theme.textMuted}`}>Loading document...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-3 text-center">
            <AlertCircle className="w-10 h-10 text-gray-400" />
            <p className={`text-base font-medium ${theme.text}`}>{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="mt-2 px-4 py-2 text-sm text-blue-500 hover:text-blue-600 font-medium"
            >
              Back to tournament
            </button>
          </div>
        ) : pdfUrl ? (
          <div className={`rounded-xl overflow-hidden border ${theme.border} shadow-sm`}>
            <iframe
              src={pdfUrl}
              title={title}
              className="w-full"
              style={{ minHeight: '80vh', border: 'none' }}
            />
          </div>
        ) : srcDoc ? (
          <div className={`rounded-xl overflow-hidden border ${theme.border} shadow-sm`}>
            <iframe
              srcDoc={srcDoc}
              title={title}
              className="w-full"
              style={{ minHeight: '80vh', border: 'none' }}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
